import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ScanSearch, Plus, Move } from 'lucide-react';
import { BoxDrawLayer, EditableBox, newWindowFromBox, isDoorType } from '../../../components/BoxDrawLayer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { salesApi, apiError, fileUrl } from '../../salesApi';
import { useT } from '../../salesI18n';
import { WindowRow } from './WindowRow';
import { OpeningFormDialog } from '../measurement/OpeningFormDialog';
import { fmtInches } from '../measurement/InchesInput';

// Caixas numeradas sobre a foto (box normalizado 0-1000). cover={w,h,aspect} ajusta p/ object-cover. labels={n: '36×60'} = janela vinculada.
// onBoxChange(n, box) → quadros editáveis (mover/redimensionar) quando ativos.
export const WindowMarkers = ({ windows = [], active, onPick, small = false, cover = null, labels = {}, onBoxChange = null }) => {
  let sx = 1, sy = 1, ox = 0, oy = 0;
  if (cover && cover.w && cover.h) {
    const a = cover.w / cover.h, b = cover.aspect || 4 / 3;
    if (a > b) { sx = a / b; ox = (1 - sx) / 2; } else { sy = b / a; oy = (1 - sy) / 2; }
  }
  return (
  <>
    {windows.map((w) => {
      const [x0, y0, x1, y1] = w.box || [0, 0, 0, 0];
      const has = x1 > x0 && y1 > y0;
      const door = isDoorType(w.type);
      const custom = w.replace === false || w.is_new || w.style || w.grille || w.glass_type || w.screen || w.exterior_trim || w.interior_trim;
      const linked = Boolean(labels[w.n]);
      const cls = w.replace === false ? 'border-slate-300/90 bg-slate-900/30' : w.is_new ? 'border-emerald-400 border-dashed bg-emerald-400/20' : door ? 'border-amber-400 bg-amber-400/15' : custom ? 'border-amber-400 bg-amber-400/15' : 'border-teal-400 bg-teal-400/10';
      const fallback = [(w.n - 1) * 120 + 40, 40, (w.n - 1) * 120 + 120, 120];
      const style = has
        ? { left: `${(ox + (x0 / 1000) * sx) * 100}%`, top: `${(oy + (y0 / 1000) * sy) * 100}%`, width: `${((x1 - x0) / 1000) * sx * 100}%`, height: `${((y1 - y0) / 1000) * sy * 100}%` }
        : { left: `${(w.n - 1) * 12 + 4}%`, top: '4%', width: '8%', height: '8%' };
      const badge = <span className={`absolute -top-2.5 -left-2.5 rounded-full text-white font-bold flex items-center justify-center shadow pointer-events-none ${small ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-xs'} ${w.replace === false ? 'bg-slate-500' : w.is_new ? 'bg-emerald-600' : door ? 'bg-amber-500' : custom ? 'bg-amber-500' : 'bg-teal-600'}`}>{w.n}</span>;
      const sizeTag = linked && <span className={`absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-md bg-white/95 text-teal-800 font-mono font-bold shadow whitespace-nowrap pointer-events-none ${small ? 'text-[8px] px-1 leading-3' : 'text-[10px] px-1.5 py-0.5'}`} data-testid={`window-marker-size-${w.n}`}>{labels[w.n]}</span>;
      if (onBoxChange) {
        return (
          <EditableBox key={w.n} box={has ? w.box : fallback} active={active === w.n} onPick={() => onPick?.(w.n)} onChange={(b) => onBoxChange(w.n, b)} className={`${cls} ${linked ? 'ring-1 ring-white/70' : ''}`} testId={`window-box-${w.n}`}>
            {badge}{sizeTag}
            {active === w.n && <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><Move className="h-4 w-4 text-white drop-shadow" /></span>}
          </EditableBox>
        );
      }
      return onPick ? (
        <button
          type="button" key={w.n} onClick={(e) => { e.stopPropagation(); onPick(w.n); }} data-testid={`window-marker-${w.n}`}
          className={`absolute border-2 rounded-md transition-all pointer-events-auto ${cls} ${linked ? 'ring-1 ring-white/70' : ''} ${active === w.n ? 'ring-2 ring-white shadow-lg' : ''}`}
          style={style}
        >{badge}{sizeTag}</button>
      ) : (
        <span key={w.n} data-testid={`window-marker-${w.n}`} className={`absolute border-2 rounded-md ${cls}`} style={style}>{badge}{sizeTag}</span>
      );
    })}
  </>
  );
};

export const markerLabels = (windows = [], openings = []) => Object.fromEntries(windows.filter((w) => w.opening_id).map((w) => {
  const o = openings.find((x) => x.id === w.opening_id);
  return o ? [w.n, `${fmtInches(o.width)}×${fmtInches(o.height)}`] : null;
}).filter(Boolean));

export const WindowConfigDialog = ({ open, onOpenChange, photo, palette, globalStyle, globalGrille, globalSpecs = {}, onSaved, openings = [], leadId, onOpeningsChanged, initialActive = null }) => {
  const { t } = useT();
  const [windows, setWindows] = useState([]);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [opDlg, setOpDlg] = useState(null); // {opening?, prefill?, forN?}
  const [drawing, setDrawing] = useState(false);

  useEffect(() => { if (open && photo) { setWindows((photo.windows || []).map((w) => ({ ...w }))); setActive(initialActive); } }, [open, photo, initialActive]);
  if (!photo || !palette) return null;

  const kind = photo.photo_kind || 'exterior';
  const upd = (n, patch) => setWindows((ws) => ws.map((w) => (w.n === n ? { ...w, ...patch } : w)));
  // Copia grade, vidro, tela e acabamentos p/ as outras janelas — NUNCA o modelo (tipo) nem a cor (a cor vem das variações)
  const applyToAll = (n) => {
    const src = windows.find((w) => w.n === n);
    if (!src) return;
    const srcDoor = isDoorType(src.type);
    setWindows((ws) => ws.map((w) => (w.n === n ? w : {
      ...w, replace: w.is_new ? true : src.replace, glass_type: src.glass_type, exterior_trim: w.is_new ? w.exterior_trim : src.exterior_trim, interior_trim: w.is_new ? w.interior_trim : src.interior_trim,
      ...(isDoorType(w.type) === srcDoor ? { grille: src.grille, screen: src.screen } : {}),
    })));
    toast.success(t.appliedToAll);
  };
  const renumber = (ws) => ws.map((w, i) => ({ ...w, n: i + 1 }));
  const addNew = (box) => { setDrawing(false); setWindows((ws) => { const next = renumber([...ws, newWindowFromBox(box, ws.length + 1)]); setActive(next.length); return next; }); };
  const removeNew = (n) => setWindows((ws) => renumber(ws.filter((w) => w.n !== n)));
  const specPalette = { ...palette, defaultGlass: globalSpecs.glass_type, defaultScreen: globalSpecs.screen, defaultExtTrim: globalSpecs.exterior_trim, defaultIntTrim: globalSpecs.interior_trim };
  const persist = async (ws, quiet = false) => {
    const { data } = await salesApi.put(`/files/${photo.id}/windows`, { windows: ws });
    if (!quiet) toast.success(t.windowsSaved);
    onSaved?.(data);
    return data;
  };

  const save = async () => {
    setSaving(true);
    try { await persist(windows); onOpenChange(false); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const redetect = async () => {
    setDetecting(true);
    try { const { data } = await salesApi.post(`/files/${photo.id}/detect-windows`); setWindows(data.windows || []); onSaved?.(data); toast.success(t.windowsDetected(data.windows?.length || 0)); } catch (e) { toast.error(apiError(e)); } finally { setDetecting(false); }
  };
  const createFor = (w) => setOpDlg({ forN: w.n, prefill: { type: w.type, notes: w.position ? `${t.windowN(w.n)} · ${w.position}` : t.windowN(w.n) } });
  const onOpeningSaved = async (data) => {
    onOpeningsChanged?.();
    if (opDlg?.forN) {
      const ws = windows.map((w) => (w.n === opDlg.forN ? { ...w, opening_id: data.id } : w));
      setWindows(ws);
      try { await persist(ws, true); toast.success(t.windowLinked); } catch (e) { toast.error(apiError(e)); }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="window-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ScanSearch className="h-5 w-5 text-teal-700" /> {t.configureWindows} <span className="text-sm font-medium text-slate-500">· {windows.length} {t.windowsLabel}</span></DialogTitle>
        </DialogHeader>
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4">
          <div>
            <div className="relative rounded-2xl overflow-hidden bg-black select-none" style={{ aspectRatio: `${photo.width || 4} / ${photo.height || 3}` }} data-testid="window-config-stage">
              <img src={fileUrl(photo.id)} alt="" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
              <WindowMarkers windows={windows} active={active} onPick={(n) => setActive((a) => (a === n ? null : n))} labels={markerLabels(windows, openings)} onBoxChange={(n, box) => upd(n, { box })} />
              <BoxDrawLayer active={drawing} onDone={addNew} onCancel={() => setDrawing(false)} hint={t.drawNewWindowHint} testId="window-draw-layer" />
            </div>
            <p className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-1" data-testid="window-drag-hint"><Move className="h-3 w-3" /> {t.dragHint}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-teal-600" /> {t.legendDefault}</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-amber-500" /> {t.legendCustom} / {t.doorBadge}</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-slate-500" /> {t.legendKeep}</span>
              <button type="button" onClick={() => setDrawing((d) => !d)} className={`ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full border font-semibold ${drawing ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-emerald-200 bg-white/70 text-emerald-800 hover:bg-emerald-50'}`} data-testid="window-add-new-btn"><Plus className="h-3.5 w-3.5" /> {drawing ? t.cancelDraw : t.addNewWindow}</button>
              <button type="button" onClick={redetect} disabled={detecting} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-teal-200 bg-white/70 text-teal-800 font-semibold hover:bg-teal-50 disabled:opacity-60" data-testid="window-redetect-btn">
                {detecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {t.redetect}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">{t.windowLinkedHint}</p>
          </div>

          <div className="space-y-2 max-h-[62vh] overflow-y-auto pr-1 sales-kanban-scroll" data-testid="window-config-list">
            {windows.length === 0 && <p className="text-sm text-slate-500 py-6 text-center" data-testid="window-config-empty">{t.noWindowsDetected}</p>}
            {windows.map((w) => (
              <WindowRow key={w.n} w={w} active={active === w.n} onActivate={setActive} upd={upd} applyToAll={applyToAll} globalStyle={globalStyle} globalGrille={globalGrille} palette={specPalette} kind={kind} onRemove={removeNew}
                openings={openings} onLink={(n, id) => upd(n, { opening_id: id })} onCreate={createFor} onOpen={(op) => setOpDlg({ opening: op })} />
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="h-10" onClick={() => onOpenChange(false)} data-testid="window-config-cancel">{t.cancel}</Button>
          <Button type="button" className="h-10 font-semibold" onClick={save} disabled={saving} data-testid="window-config-save">{saving ? t.loading : t.save}</Button>
        </div>
        <OpeningFormDialog open={Boolean(opDlg)} onOpenChange={(o) => !o && setOpDlg(null)} leadId={leadId} opening={opDlg?.opening || null} prefill={opDlg?.prefill} onSaved={onOpeningSaved} />
      </DialogContent>
    </Dialog>
  );
};
